import { Link, useLocation } from 'react-router';
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CalendarDays, BarChart3, Bell, Settings } from 'lucide-react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { notificationsInboxApi } from '@/api/notifications';
import { useSseSubscription } from '@/hooks/useSseSubscription';

const NOTIFICATIONS_BASE_URL =
  (import.meta.env.VITE_NOTIFICATIONS_URL as string | undefined) || 'http://localhost:8084';

const FALLBACK_REFETCH_MS = 5 * 60 * 1000;

export function BottomNav() {
  const location = useLocation();
  const businessId = useBusinessId();

  const queryClient = useQueryClient();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread', businessId],
    queryFn: () => notificationsInboxApi.unreadCount(businessId),
    enabled: !!businessId,
    refetchInterval: FALLBACK_REFETCH_MS,
  });

  const refreshNotifications = useCallback(() => {
    if (!businessId) return;
    void queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
    void queryClient.invalidateQueries({ queryKey: ['notifications-unread', businessId] });
  }, [queryClient, businessId]);

  useSseSubscription({
    url: businessId
      ? `${NOTIFICATIONS_BASE_URL}/api/v1/businesses/${businessId}/notifications/stream`
      : null,
    onEvent: refreshNotifications,
    onOpen: refreshNotifications,
    deps: [businessId],
  });

  const tabs = [
    { label: 'Hoy', path: '/', icon: CalendarCheck, badge: 0 },
    { label: 'Citas', path: '/appointments', icon: CalendarDays, badge: 0 },
    { label: 'Dashboard', path: '/insights', icon: BarChart3, badge: 0 },
    { label: 'Alertas', path: '/notifications', icon: Bell, badge: unreadCount },
    { label: 'Ajustes', path: '/settings', icon: Settings, badge: 0 },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="fixed bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full"
      style={{
        backgroundColor: 'var(--dark-teal)',
        padding: '6px 8px',
        zIndex: 100,
        boxShadow: '0 4px 20px rgba(36, 62, 61, 0.25)',
      }}
    >
      {tabs.map(tab => {
        const Icon = tab.icon;
        const active = isActive(tab.path);

        return (
          <Link
            key={tab.path}
            to={tab.path}
            className="relative flex items-center gap-1.5 transition-all"
            style={{
              padding: active ? '8px 16px' : '8px 12px',
              borderRadius: '999px',
              backgroundColor: active ? 'var(--terracotta)' : 'transparent',
              color: active ? 'var(--off-white)' : 'rgba(255, 255, 255, 0.5)',
              minHeight: '36px',
            }}
          >
            <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
            {active && (
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: 'var(--font-body)',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </span>
            )}
            {/* Unread badge */}
            {tab.badge > 0 && !active && (
              <span
                className="absolute flex items-center justify-center rounded-full"
                style={{
                  top: '2px',
                  right: '4px',
                  minWidth: '16px',
                  height: '16px',
                  padding: '0 4px',
                  backgroundColor: '#E07B54',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {tab.badge > 9 ? '9+' : tab.badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
